import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Stack
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { documentsApi } from '../../services/documentsApi';
import { Document, DocumentType, DocumentFileType, DocumentCategory } from '../../types/documents';

interface DocumentUploadProps {
  clientId: string;
  onUploadComplete?: (document: Document) => void;
}

interface UploadedFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
  documentId?: string;
}

const DOCUMENT_TYPES = [
  { value: 'financial_statement', label: 'Financial Statement' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'tax_return', label: 'Tax Return' },
  { value: 'identification', label: 'Identification' },
  { value: 'business_license', label: 'Business License' },
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' }
];

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  clientId,
  onUploadComplete
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [defaultDocumentType, setDefaultDocumentType] = useState<DocumentType>('other');
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [autoProcess, setAutoProcess] = useState(true);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = acceptedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
    
    // Start uploading files
    const currentFilesLength = files.length;
    newFiles.forEach((uploadFile, index) => {
      uploadDocument(currentFilesLength + index, uploadFile);
    });
  }, [files.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const uploadDocument = async (fileIndex: number, uploadFile: UploadedFile) => {
    try {
      // Update status to uploading
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { ...f, status: 'uploading', progress: 10 } : f
      ));

      // Get upload URL
      const uploadUrlResponse = await documentsApi.getUploadUrl({
        fileName: uploadFile.file.name,
        fileType: getFileExtension(uploadFile.file.name),
        fileSize: uploadFile.file.size,
        clientId
      });

      // Update progress
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { ...f, progress: 30 } : f
      ));

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrlResponse.uploadUrl, {
        method: 'PUT',
        body: uploadFile.file,
        headers: {
          'Content-Type': uploadFile.file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Update progress
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { ...f, progress: 60 } : f
      ));

      // Create document record
      const documentData = {
        fileName: uploadFile.file.name,
        fileSize: uploadFile.file.size,
        fileType: getFileExtension(uploadFile.file.name),
        mimeType: uploadFile.file.type,
        s3Key: uploadUrlResponse.s3Key,
        s3Bucket: uploadUrlResponse.s3Bucket,
        clientId,
        documentType: defaultDocumentType,
        category: getCategoryFromType(defaultDocumentType),
        workflow: {
          requiresSignature,
          autoProcess
        }
      };

      const document = await documentsApi.createDocument(documentData);

      // Update to processing status
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { 
          ...f, 
          status: 'processing', 
          progress: 80,
          documentId: document.id 
        } : f
      ));

      // Start processing if auto-process is enabled
      if (autoProcess) {
        await documentsApi.processDocument(document.id);
      }

      // Mark as complete
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { ...f, status: 'complete', progress: 100 } : f
      ));

      onUploadComplete?.(document);

    } catch (error) {
      console.error('Upload failed:', error);
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? { 
          ...f, 
          status: 'error', 
          error: error instanceof Error ? error.message : 'Upload failed' 
        } : f
      ));
    }
  };

  const getFileExtension = (filename: string): DocumentFileType => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const validExtensions: DocumentFileType[] = ['pdf', 'jpg', 'jpeg', 'png', 'tiff', 'doc', 'docx'];
    return validExtensions.includes(ext as DocumentFileType) ? (ext as DocumentFileType) : 'pdf';
  };

  const getCategoryFromType = (documentType: DocumentType): DocumentCategory => {
    const mapping: Record<DocumentType, DocumentCategory> = {
      'financial_statement': 'financial',
      'bank_statement': 'financial',
      'tax_return': 'financial',
      'invoice': 'financial',
      'receipt': 'financial',
      'identification': 'identity',
      'business_license': 'business',
      'contract': 'legal',
      'insurance': 'compliance',
      'other': 'other'
    };
    return mapping[documentType] || 'other';
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'complete':
        return <CompleteIcon color="success" />;
      case 'error':
        return <DocumentIcon color="error" />;
      default:
        return <DocumentIcon color="primary" />;
    }
  };

  const getStatusColor = (status: UploadedFile['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'error':
        return 'error';
      case 'processing':
        return 'info';
      case 'uploading':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Documents
          </Typography>

          {/* Configuration Options */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Default Document Type</InputLabel>
              <Select
                value={defaultDocumentType}
                onChange={(e) => setDefaultDocumentType(e.target.value as DocumentType)}
                label="Default Document Type"
              >
                {DOCUMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={requiresSignature}
                  onChange={(e) => setRequiresSignature(e.target.checked)}
                />
              }
              label="Requires Signature"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={autoProcess}
                  onChange={(e) => setAutoProcess(e.target.checked)}
                />
              }
              label="Auto Process"
            />
          </Stack>

          {/* Drop Zone */}
          <Box
            {...getRootProps()}
            sx={{
              border: 2,
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderStyle: 'dashed',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or click to select files
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported: PDF, JPG, PNG, TIFF, DOC, DOCX (max 50MB)
            </Typography>
          </Box>

          {/* File List */}
          {files.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Upload Progress
              </Typography>
              {files.map((uploadFile, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" flex={1}>
                        {getStatusIcon(uploadFile.status)}
                        <Box sx={{ ml: 2, flex: 1 }}>
                          <Typography variant="body2" noWrap>
                            {uploadFile.file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(uploadFile.file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={uploadFile.status}
                          color={getStatusColor(uploadFile.status)}
                          size="small"
                        />
                        {uploadFile.status !== 'complete' && uploadFile.status !== 'error' && (
                          <Button
                            size="small"
                            onClick={() => removeFile(index)}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {(uploadFile.status === 'uploading' || uploadFile.status === 'processing') && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadFile.progress}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {uploadFile.progress}% - {
                            uploadFile.status === 'uploading' ? 'Uploading...' : 'Processing...'
                          }
                        </Typography>
                      </Box>
                    )}

                    {uploadFile.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {uploadFile.error}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentUpload;
